import { useMemo, useState } from 'react';
import {
  ActionIcon,
  Box,
  Button,
  Center,
  Divider,
  Group,
  Loader,
  Stack,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { HiCheck, HiOutlineDocumentDuplicate, HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi2';

import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import StyledTextInput from '@/components/StyledInputFields/StyledTextInput.tsx';
import AppIcon from '@/components/AppIcon.tsx';
import useFieldConfigError from '@/hooks/useFieldConfigError.ts';
import useCreateEmailAlias from '@/hooks/api/integrations/useCreateEmailAlias.ts';
import useDeleteEmailAlias from '@/hooks/api/integrations/useDeleteEmailAlias.ts';
import useRetrieveEmailRoutes from '@/hooks/api/integrations/useRetrieveEmailRoutes.ts';
import useUpdateEmailAlias from '@/hooks/api/integrations/useUpdateEmailAlias.ts';
import { ConfigFieldValue } from '@/types/automationFields.ts';
import { EmailRoute, IntegrationTextConfigField } from '@/types/integrations.ts';
import { getHttpErrorMessage } from '@/services/httpRetry.ts';

type Props = {
  stepId: string;
  field: IntegrationTextConfigField;
  fieldValue: any;
  fieldValueConfig: ConfigFieldValue;
  onChange: (updated_value: any) => void;
};

const EmailField = ({ stepId, field, fieldValue, fieldValueConfig }: Props) => {
  const error = useFieldConfigError(stepId, fieldValueConfig);
  const { data: emailRoutes, isLoading } = useRetrieveEmailRoutes(stepId, !fieldValueConfig.is_disabled);
  const { mutate: createAlias, isPending: isCreatingAlias } = useCreateEmailAlias();
  const { mutate: updateAlias, isPending: isUpdatingAlias } = useUpdateEmailAlias();
  const { mutate: deleteAlias, isPending: isDeletingAlias } = useDeleteEmailAlias();

  const [newAlias, setNewAlias] = useState('');
  const [editingRouteId, setEditingRouteId] = useState<string | null>(null);
  const [editingAliasValue, setEditingAliasValue] = useState('');
  const [showCopiedCheck, setShowCopiedCheck] = useState(false);

  const generatedEmailAddress = emailRoutes?.generated_route.email_address || fieldValue || '';
  const aliases = useMemo(() => emailRoutes?.aliases || [], [emailRoutes?.aliases]);
  const mutationsDisabled = isCreatingAlias || isUpdatingAlias || isDeletingAlias;

  const isAliasManagerDisabled = useMemo(
    () => fieldValueConfig.is_disabled || mutationsDisabled,
    [fieldValueConfig.is_disabled, mutationsDisabled],
  );

  const handleCopy = async (value: string, message: string) => {
    try {
      await navigator.clipboard.writeText(value);
      notifications.show({ message, color: 'blue' });
      setShowCopiedCheck(true);
      setTimeout(() => setShowCopiedCheck(false), 3000);
    } catch {
      notifications.show({ message: 'Could not copy email address', color: 'red' });
    }
  };

  const handleCreateAlias = () => {
    const trimmedAlias = newAlias.trim();
    if (!trimmedAlias) return;

    createAlias(
      { stepId, localPart: trimmedAlias },
      {
        onSuccess: () => {
          setNewAlias('');
          notifications.show({ message: 'Email alias added', color: 'blue' });
        },
        onError: (error) => {
          notifications.show({ message: getHttpErrorMessage(error), color: 'red' });
        },
      },
    );
  };

  const handleSaveAlias = (route: EmailRoute) => {
    const trimmedAlias = editingAliasValue.trim();
    if (!trimmedAlias) return;

    updateAlias(
      { stepId, routeId: route.id, localPart: trimmedAlias },
      {
        onSuccess: () => {
          handleCancelEditingAlias();
          notifications.show({ message: 'Email alias updated', color: 'blue' });
        },
        onError: (error) => {
          notifications.show({ message: getHttpErrorMessage(error), color: 'red' });
        },
      },
    );
  };

  const handleStartEditingAlias = (route: EmailRoute) => {
    setEditingRouteId(route.id);
    setEditingAliasValue(route.local_part);
  };

  const handleCancelEditingAlias = () => {
    setEditingRouteId(null);
    setEditingAliasValue('');
  };

  const handleDeleteAlias = (route: EmailRoute) => {
    deleteAlias(
      { stepId, routeId: route.id },
      {
        onSuccess: () => {
          if (editingRouteId === route.id) {
            handleCancelEditingAlias();
          }
          notifications.show({ message: 'Email alias removed', color: 'blue' });
        },
        onError: (error) => {
          notifications.show({ message: getHttpErrorMessage(error), color: 'red' });
        },
      },
    );
  };

  return (
    <Stack gap="sm">
      <Box pos="relative">
        <StyledTextInput
          label={field.display_name}
          value={generatedEmailAddress}
          description={field.description ?? undefined}
          error={error?.message}
          disabled={fieldValueConfig.is_disabled}
          readOnly={true}
          rightSection={
            <Center>
              <StyledButton
                variant="subtleIcon"
                size="sm"
                aria-label="Copy generated email address"
                style={{
                  height: '25px',
                  width: '25px',
                  backgroundColor: showCopiedCheck ? 'transparent' : '#F6F6F6',
                  color: '#757575',
                  borderRadius: '50%',
                }}
                disabled={!generatedEmailAddress || showCopiedCheck}
                onClick={() => handleCopy(generatedEmailAddress, 'Email address copied to clipboard')}
              >
                <AppIcon Icon={showCopiedCheck ? HiCheck : HiOutlineDocumentDuplicate} size={16} />
              </StyledButton>
            </Center>
          }
        />
      </Box>

      <Divider />

      <Stack gap="xs">
        <Group justify="space-between" align="center">
          <Text size="sm" fw={500}>
            Human-readable aliases
          </Text>
          <Text size="xs" c="dimmed">
            Multiple aliases can point to the same trigger
          </Text>
        </Group>

        <Text size="xs" c="dimmed">
          The generated address always works. Add aliases when you want cleaner addresses or need a migration period.
        </Text>

        {isLoading ? (
          <Center py="sm">
            <Loader size="sm" />
          </Center>
        ) : (
          <Stack gap="xs">
            {aliases.map((alias) => {
              const isEditing = editingRouteId === alias.id;
              const currentLocalPart = isEditing ? editingAliasValue : alias.local_part;
              const currentEmailAddress = `${currentLocalPart || alias.local_part}@${alias.email_address.split('@')[1]}`;

              return (
                <Box
                  key={alias.id}
                  p="sm"
                  style={{
                    border: '1px solid #ececed',
                    borderRadius: 10,
                    background: '#fbfbfb',
                  }}
                >
                  <Stack gap={6}>
                    <Group align="flex-start" justify="space-between" wrap="nowrap">
                      <Box style={{ flex: 1 }}>
                        <StyledTextInput
                          label={isEditing ? 'Alias local-part' : undefined}
                          aria-label={!isEditing ? `Alias local-part ${alias.local_part}` : undefined}
                          value={currentLocalPart}
                          onChange={(event) => setEditingAliasValue(event.currentTarget.value)}
                          readOnly={!isEditing}
                          disabled={isAliasManagerDisabled}
                        />
                      </Box>
                      <Group gap={4} wrap="nowrap">
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          aria-label={`Copy alias ${alias.local_part}`}
                          onClick={() => handleCopy(currentEmailAddress, 'Alias copied to clipboard')}
                          disabled={isAliasManagerDisabled}
                        >
                          <AppIcon Icon={HiOutlineDocumentDuplicate} size={16} />
                        </ActionIcon>
                        {isEditing ? (
                          <>
                            <Button
                              size="xs"
                              variant="light"
                              onClick={() => handleSaveAlias(alias)}
                              disabled={isAliasManagerDisabled || !editingAliasValue.trim()}
                            >
                              Save
                            </Button>
                            <Button
                              size="xs"
                              variant="subtle"
                              color="gray"
                              onClick={handleCancelEditingAlias}
                              disabled={isAliasManagerDisabled}
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <>
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              aria-label={`Edit alias ${alias.local_part}`}
                              onClick={() => handleStartEditingAlias(alias)}
                              disabled={isAliasManagerDisabled}
                            >
                              <AppIcon Icon={HiOutlinePencil} size={16} />
                            </ActionIcon>
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              aria-label={`Delete alias ${alias.local_part}`}
                              onClick={() => handleDeleteAlias(alias)}
                              disabled={isAliasManagerDisabled}
                            >
                              <AppIcon Icon={HiOutlineTrash} size={16} />
                            </ActionIcon>
                          </>
                        )}
                      </Group>
                    </Group>
                    <Text size="xs" c="dimmed">
                      {isEditing ? currentEmailAddress : alias.email_address}
                    </Text>
                  </Stack>
                </Box>
              );
            })}

            <Box
              p="sm"
              style={{
                border: '1px dashed #d8d8dc',
                borderRadius: 10,
                background: '#fcfcfd',
              }}
            >
              <Stack gap="xs">
                <Text size="sm" fw={500}>
                  Add alias
                </Text>
                <Group align="flex-end" wrap="nowrap">
                  <Box style={{ flex: 1 }}>
                    <StyledTextInput
                      label="Alias local-part"
                      placeholder="claims-intake"
                      value={newAlias}
                      onChange={(event) => setNewAlias(event.currentTarget.value)}
                      disabled={isAliasManagerDisabled}
                    />
                  </Box>
                  <Button
                    onClick={handleCreateAlias}
                    disabled={isAliasManagerDisabled || !newAlias.trim()}
                    loading={isCreatingAlias}
                  >
                    Add alias
                  </Button>
                </Group>
              </Stack>
            </Box>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default EmailField;
